import type { FlagType } from '@/data-store/zustand/slices/globalSlice';
import { getOrCreateRolloutBucket } from '@/utils/featureFlags/rolloutFeatureFlags';
import { EXPERIMENT_BUCKET_COOKIES, FEATURE_FLAGS } from './constants';
import { asStateRolloutPercents, isInRollout } from './featureFlagHelpers';

export const TEST_OVERRIDE_QUERY_PARAM = 'first-look';
export const TEST_OVERRIDE_FEATURES = {
  ADD_ON_BATTERY: 'add-on-battery',
} as const;

const FIRST_LOOK_SESSION_KEY = 'public_retrofit_first_look';

export function getOrPersistFirstLookOverride(
  queryValue: string | string[] | undefined
): boolean {
  // The gate calls this during render of a statically exported page
  if (typeof window === 'undefined') return false;
  if (queryValue === TEST_OVERRIDE_FEATURES.ADD_ON_BATTERY) {
    sessionStorage.setItem(FIRST_LOOK_SESSION_KEY, 'true');
    return true;
  }
  return sessionStorage.getItem(FIRST_LOOK_SESSION_KEY) === 'true';
}

export type PublicRetrofitRolloutAssignment = {
  inRollout: boolean;
  rolloutPercent: number;
  bucket: number | undefined;
};

const EXCLUDED: PublicRetrofitRolloutAssignment = {
  inRollout: false,
  rolloutPercent: 0,
  bucket: undefined,
};

export function getPublicRetrofitRolloutAssignment(
  featureFlags: FlagType,
  userState: string | null,
  includeInRollout = false
): PublicRetrofitRolloutAssignment {
  if (!featureFlags) return EXCLUDED;

  // The first-look override includes the visitor regardless of market gating
  // or the A/B bucket, so no bucket is assigned. It must not depend on geo:
  // a failed geo lookup leaves the state null and would otherwise redirect.
  if (includeInRollout) {
    const marketRolloutPercentage = userState
      ? asStateRolloutPercents(
          featureFlags[FEATURE_FLAGS.PUBLIC_RETROFIT_ROLLOUT]
        )?.[userState.toUpperCase()]
      : undefined;
    return {
      inRollout: true,
      rolloutPercent: marketRolloutPercentage ?? 0,
      bucket: undefined,
    };
  }

  if (!userState) return EXCLUDED;

  const rollouts = asStateRolloutPercents(
    featureFlags[FEATURE_FLAGS.PUBLIC_RETROFIT_ROLLOUT]
  );
  const marketRolloutPercentage = rollouts?.[userState.toUpperCase()];

  // Does not assign a bucket if we have rolled out to 100% or 0% of market.
  if (!marketRolloutPercentage || marketRolloutPercentage <= 0) {
    return EXCLUDED;
  }

  if (marketRolloutPercentage >= 100) {
    return {
      inRollout: true,
      rolloutPercent: marketRolloutPercentage,
      bucket: undefined,
    };
  }

  const bucket = getOrCreateRolloutBucket(
    EXPERIMENT_BUCKET_COOKIES.PUBLIC_RETROFIT
  );

  return {
    inRollout: isInRollout(marketRolloutPercentage, bucket),
    rolloutPercent: marketRolloutPercentage,
    bucket,
  };
}
